import React, { useState } from 'react'
import { PiggyBank, TrendingUp, HeartPulse, FileClock, LayoutGrid, Users, Sparkles, Globe } from 'lucide-react'
import BottomMenu from './BottomMenu'
import OptionCard from './OptionCard'

interface GoalOption {
  value: string
  title: string
  description: string
  icon: React.ReactNode
  tile: string
}

const STORE_GOALS: GoalOption[] = [
  { value: 'profitability', title: 'Understand true profitability', description: 'See net margin after COGS, fees, shipping, and ad spend — across every channel', icon: <PiggyBank className="tw-h-5 tw-w-5" />, tile: 'tw-bg-emerald-50 tw-text-emerald-600' },
  { value: 'ad_roi', title: 'Scale advertising ROI', description: 'Unify Google, Meta, and TikTok spend to find what’s driving real revenue', icon: <TrendingUp className="tw-h-5 tw-w-5" />, tile: 'tw-bg-rose-50 tw-text-rose-600' },
  { value: 'churn', title: 'Reduce customer churn', description: 'Track repeat purchase rates, LTV, and at-risk segments automatically', icon: <HeartPulse className="tw-h-5 tw-w-5" />, tile: 'tw-bg-orange-50 tw-text-orange-600' },
  { value: 'automate_reporting', title: 'Automate reporting', description: 'Replace manual spreadsheets with daily snapshots sent to your inbox', icon: <FileClock className="tw-h-5 tw-w-5" />, tile: 'tw-bg-blue-50 tw-text-blue-600' },
  { value: 'unify_data', title: 'Unify data across channels', description: 'One dashboard for Shopify, Amazon, eBay, BigCommerce, and more', icon: <LayoutGrid className="tw-h-5 tw-w-5" />, tile: 'tw-bg-indigo-50 tw-text-indigo-600' },
  { value: 'team_efficiency', title: 'Grow team efficiency', description: 'Give every team member the exact metrics they need, nothing more', icon: <Users className="tw-h-5 tw-w-5" />, tile: 'tw-bg-purple-50 tw-text-purple-600' },
]

const STORELESS_GOALS: GoalOption[] = [
  { value: 'ai_site', title: 'Edit my site with AI', description: 'Update posts, pages, media, theme, and styling on your WordPress site with the AI assistant', icon: <Sparkles className="tw-h-5 tw-w-5" />, tile: 'tw-bg-blue-50 tw-text-blue-600' },
  { value: 'traffic_ad_roi', title: 'Grow traffic & ad ROI', description: 'Track sessions, funnels, and ad spend across Google, Meta, and TikTok', icon: <TrendingUp className="tw-h-5 tw-w-5" />, tile: 'tw-bg-rose-50 tw-text-rose-600' },
  { value: 'explore_ai', title: 'Explore the AI assistant', description: 'Ask questions, generate content, and automate tasks — no store required', icon: <Sparkles className="tw-h-5 tw-w-5" />, tile: 'tw-bg-indigo-50 tw-text-indigo-600' },
  { value: 'automate_reporting', title: 'Automate reporting', description: 'Replace manual spreadsheets with daily snapshots sent to your inbox', icon: <FileClock className="tw-h-5 tw-w-5" />, tile: 'tw-bg-slate-100 tw-text-slate-600' },
  { value: 'unify_marketing', title: 'Unify marketing data', description: 'One place for analytics, ads, email, and traffic', icon: <Globe className="tw-h-5 tw-w-5" />, tile: 'tw-bg-teal-50 tw-text-teal-600' },
  { value: 'team_efficiency', title: 'Grow team efficiency', description: 'Give every team member the exact metrics they need, nothing more', icon: <Users className="tw-h-5 tw-w-5" />, tile: 'tw-bg-purple-50 tw-text-purple-600' },
]

interface BusinessGoalProps {
  storeless?: boolean
  initialValue?: string | null
  onNext: (value: string) => void
  onBack?: () => void
}

/** #693: "What's your primary goal?" — single-select, path-aware options. */
const BusinessGoal: React.FC<BusinessGoalProps> = ({ storeless = false, initialValue = null, onNext, onBack }) => {
  const [selected, setSelected] = useState<string | null>(initialValue)
  const goals = storeless ? STORELESS_GOALS : STORE_GOALS

  return (
    <div className="tw-p-4 md:tw-p-8 tw-pb-32">
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
        {goals.map((g) => (
          <OptionCard
            key={g.value}
            horizontal
            selected={selected === g.value}
            onClick={() => setSelected(g.value)}
            icon={g.icon}
            tile={g.tile}
            title={g.title}
            description={g.description}
          />
        ))}
      </div>
      <BottomMenu onPrev={onBack} onNext={() => selected && onNext(selected)} disableNext={!selected} />
    </div>
  )
}

export default BusinessGoal
